// The bank cards on the authorisation step.
//
// One card per bank the customer has IBANs at: its logo, the IBAN, a line that
// tracks progress and a line under it for detail. The card itself is the link
// the customer clicks to authorise at that bank, so its href is filled in later
// once the bank has told us where to send them.
//
// The info icon opens the sandbox PSU login for that bank. Outside a sandbox
// the server sends none, and the popup says none are needed.
(function (global) {
  "use strict";

  const $ = global.$;
  const UNSUPPORTED_BANK = "Banka nije podržana";

  // The info button beside each card. Its size is shared with infoSpacer(),
  // which holds the same space empty, so anything stacked under the cards -
  // the email form - lines up with them exactly instead of running wider.
  const INFO_BUTTON_CLASS = "bg-transparent p-1.5";
  const INFO_BUTTON_CSS = { "font-size": "24px", "margin-left": "16px" };
  const INFO_ICON = '<i class="bi bi-info-circle"></i>';

  function infoSpacer() {
    return $("<span>", {
      class: INFO_BUTTON_CLASS + " invisible",
      css: INFO_BUTTON_CSS,
      "aria-hidden": "true",
    }).html(INFO_ICON);
  }

  // Logo file per bank comes from the server (window.BANK_LOGOS), built from
  // the bank registry - the front end used to keep its own map, which was a
  // third copy of bank knowledge and one more thing to update per bank.
  function logoFor(bankName) {
    const logos = global.BANK_LOGOS || {};
    return logos[bankName] || "unknown_bank_logo.svg";
  }

  // "HR67 2500 0090 0020 7117 3" - a 21 character run is hard to check at a
  // glance, and every bank prints them this way.
  function groupIban(iban) {
    return String(iban || "").replace(/(.{4})/g, "$1 ").trim();
  }

  // The two lines on a card that change as the journey runs.
  const bankState = (frame) => $(frame).find(".bank-state");
  const bankDetail = (frame) => $(frame).find(".bank-detail");

  // Every state a card can be in, in one table.
  //
  // A state is four things that have to change together - the wording, the
  // pill around it, the left edge of the card and whether the card can be
  // clicked - so they are declared here rather than spelled out at each call
  // site, the same way the stepper keeps its own.
  //
  // Only the customer's turn is clickable. Before it the card has nowhere to
  // go yet, and after it there is nothing left to do. Its hover is the same
  // amber as its edge: the card used to carry a blue hover for every state,
  // and a hover outranks a plain edge colour, so the amber edge turned blue
  // under the mouse - just as the customer went to click it.
  //
  // Colour says whose move it is. Grey: queued. Amber: your turn - click the
  // card and authorise at the bank. Blue: we are working, wait. Green and
  // red: finished. Authorising and processing used to share the blue, so a
  // card that needed the customer looked just like one that did not.
  //
  // The wording still separates every state, not the colour alone:
  // "Dohvaćeno" and "Neuspješno" read differently to the roughly one man in
  // twelve who cannot tell the green from the red. The pill carries a ring as
  // well as a fill, because the soft fills alone are too pale to hold an edge
  // on white.
  //
  // Class names are written out in full. Tailwind scans for complete strings,
  // so a name built by joining pieces compiles to nothing and the card simply
  // never changes.
  const STATUS = {
    preparing: {
      label: "Priprema",
      pill: "bg-ground text-text-muted ring-border-strong",
      edge: "border-l-border-strong",
      card: "pointer-events-none",
    },
    // Revolut and OTP need the customer to grant us access before the
    // consent itself; like authorise, the card is waiting for a click.
    preauthorise: {
      label: "Pripremite pristup",
      pill: "bg-attention-soft text-attention-text ring-attention/40",
      edge: "border-l-attention",
      card: "cursor-pointer hover:border-attention",
    },
    authorise: {
      label: "Autorizirajte",
      pill: "bg-attention-soft text-attention-text ring-attention/40",
      edge: "border-l-attention",
      card: "cursor-pointer hover:border-attention",
    },
    fetching: {
      label: "Dohvaćam",
      pill: "bg-primary-soft text-primary ring-primary/30",
      edge: "border-l-primary",
      card: "pointer-events-none",
    },
    done: {
      label: "Dohvaćeno",
      pill: "bg-success-soft text-success-text ring-success/30",
      edge: "border-l-success",
      card: "pointer-events-none",
    },
    failed: {
      label: "Neuspješno",
      pill: "bg-danger-soft text-danger-text ring-danger/30",
      edge: "border-l-danger",
      card: "pointer-events-none",
    },
  };

  // Once a bank is done or has failed, its countdown has nothing left to say.
  const SETTLED = ["done", "failed"];

  const allOf = (key) => Object.keys(STATUS).map((k) => STATUS[k][key]).join(" ");

  function setBankStatus(frame, name) {
    const state = STATUS[name];
    if (!state) return;

    $(frame).removeClass(allOf("edge")).addClass(state.edge);
    $(frame).removeClass(allOf("card")).addClass(state.card);

    bankState(frame)
      .removeClass(allOf("pill"))
      .addClass(state.pill)
      .text(state.label);

    if (SETTLED.indexOf(name) !== -1) {
      bankDetail(frame).text("");
    }
  }

  function createBankElements(response) {
    var counter = 1;
    $.each(response.data, function (bankName, ibanArray) {
      var logoUrl = "static/images/" + logoFor(bankName);
      if (bankName === UNSUPPORTED_BANK) {
        logoUrl = "static/images/unknown_bank_logo.svg";
      }
      // The card is the link the customer clicks to authorise at this bank;
      // its href arrives later, once the bank has said where to send them.
      // The left edge carries the state, so a finished bank reads calmly
      // instead of the whole card turning green.
      var newDiv = $("<a>", {
        class:
          "bank-card flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 flex-1 basis-0 " +
          "min-w-0 bg-surface border border-border border-l-[3px] border-l-border-strong " +
          "rounded-card p-4 sm:p-5 shadow-card no-underline",
        target: "_blank",
        id: "frame-" + counter,
        "aria-live": "polite",
      });

      var img = $("<img>", {
        src: logoUrl,
        alt: "",
        class: "w-10 h-10 sm:w-12 sm:h-12 rounded-field object-contain shrink-0",
      });

      // Classes, not ids: this runs once per bank, so an id here put up to
      // five elements with the same id in the document, and the lines below
      // were then addressed by position with :nth-child.
      var textContainer = $("<div>", {
        class: "bank-card-text min-w-0 flex-1 basis-0 text-left",
      });

      var p1 = $("<p>", {
        class: "bank-name m-0 text-sm sm:text-base font-medium truncate",
        text: bankName,
      });

      // Grouped in fours: a 21 character run is genuinely hard to check.
      var p2 = $("<p>", {
        class: "bank-iban m-0 mt-0.5 sm:mt-1 text-xs sm:text-sm text-text-muted tracking-wide truncate",
        text: groupIban(ibanArray[0]),
      });

      // The pill, with the countdown centred under it while the bank is
      // waiting. The pair is grouped so the countdown centres on the pill
      // rather than on the card, and the group sits at the right.
      var status = $("<div>", {
        class: "bank-status shrink-0 flex justify-end w-full sm:w-auto",
      });
      var statusGroup = $("<div>", {
        class: "flex flex-col items-center gap-1",
      });

      var p3 = $("<span>", {
        class:
          "bank-state inline-flex items-center rounded-full px-3 py-1 text-xs " +
          "font-semibold whitespace-nowrap ring-1 ring-inset",
      });

      // empty:hidden - with no countdown the line takes no room, so the
      // pill sits on the card's centre line instead of a gap's width above.
      var p4 = $("<p>", {
        class: "bank-detail m-0 text-[11px] text-text-subtle whitespace-nowrap empty:hidden",
        text: "",
      });

      statusGroup.append(p3, p4);
      status.append(statusGroup);

      textContainer.append(p1, p2);
      newDiv.append(img, textContainer, status);

      var infoIcon = $("<a>", {
        class:
          INFO_BUTTON_CLASS +
          " cursor-pointer text-text-subtle hover:text-primary transition-colors",
        css: INFO_BUTTON_CSS,
        click: function (event) {
          // Prevent any other click event listeners from being triggered
          event.stopPropagation();

          // --- START: NEW MODERN POPUP LOGIC ---

          // Helper function to create a row with a copy button
          function createCopyableRow(label, value) {
            const valueId = `copy-val-${Math.random().toString(36).substr(2, 9)}`;
            const copyButtonId = `copy-btn-${Math.random().toString(36).substr(2, 9)}`;

            const row = $(`
              <div class="flex items-center justify-between mt-2 text-left">
                <div class="flex items-center">
                  <span class="text-sm text-text-muted w-28">${label}:</span>
                  <code id="${valueId}" class="text-sm font-mono bg-ground text-text px-2 py-1 rounded">${value}</code>
                </div>
                <button id="${copyButtonId}" class="p-2 text-text-subtle hover:text-primary transition-colors">
                  <i class="far fa-copy"></i>
                </button>
              </div>
            `);

            row.find(`#${copyButtonId}`).on('click', function() {
              const textToCopy = $(`#${valueId}`).text();
              navigator.clipboard.writeText(textToCopy).then(() => {
                // Success feedback
                const icon = $(this).find('i');
                icon.removeClass('fa-copy').addClass('fa-check text-success');
                setTimeout(() => {
                  icon.removeClass('fa-check text-success').addClass('fa-copy');
                }, 1500);
              });
            });

            return row;
          }

          // Main overlay
          var overlay = $("<div>", {
            class: "fixed inset-0 bg-text/40 backdrop-blur-sm flex items-center justify-center z-50 p-4",
            click: function (event) {
              if (event.target === this) {
                overlay.remove();
              }
            },
          });

          // The popup card
          var newFrame = $("<div>", {
            class: "bg-surface shadow-xl p-6 rounded-card relative w-full max-w-md",
          });

          // Header
          var header = $('<h2 class="text-base font-semibold text-center border-b border-border pb-3">PSU Sandbox Podaci</h2>');
          newFrame.append(header);

          // Content
          if (psuData[bankName]) {
            const usernameRow = createCopyableRow("Korisničko ime", psuData[bankName]["username"]);
            newFrame.append(usernameRow);

            if (psuData[bankName]["password"]) {
              const passwordRow = createCopyableRow("Lozinka", psuData[bankName]["password"]);
              newFrame.append(passwordRow);
            }
          } else {
            newFrame.append('<p class="mt-4 text-center text-sm text-text-muted">Login podaci nisu potrebni!</p>');
          }

          // Close button
          var closeButton = $("<button>", {
            html: "&times;",
            class: "absolute top-2 right-3 text-text-subtle hover:text-text text-3xl leading-none bg-transparent border-none cursor-pointer",
            click: function () {
              overlay.remove();
            },
          });

          newFrame.append(closeButton);
          overlay.append(newFrame);
          $("body").append(overlay);

          // --- END: NEW MODERN POPUP LOGIC ---
        },
      }).html(INFO_ICON);


      var parentContainer = $("<div>", {
        class: "flex items-center justify-between",
        "data-bank-name": bankName,
      });

      parentContainer.append(newDiv, infoIcon);
      $(".frame-container").append(parentContainer);
      setBankStatus(newDiv, "preparing");
      counter++;
    });
  }

  const api = {
    createBankElements,
    setBankStatus,
    infoSpacer,
    bankState,
    bankDetail,
    logoFor,
    STATUS,
  };
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
  Object.assign(global, api);
})(typeof window !== "undefined" ? window : globalThis);
